import { useState, useEffect, useRef } from "react";
import { api } from "../App";

export default function UserAutocomplete({ value, onChange, label, placeholder = "Search team members...", required }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  // Active row index for keyboard navigation. -1 = nothing highlighted yet.
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    api.get("/users/search").then(r => {
      const list = r.data || [];
      setUsers(list);
      if (value) {
        const found = list.find(u => u.email === value || u.user_id === value);
        if (found) setSelectedName(found.name || found.email || "");
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (value && users.length > 0) {
      const found = users.find(u => u.email === value || u.user_id === value);
      if (found) setSelectedName(found.name || found.email || "");
    } else if (!value) {
      setSelectedName("");
    }
  }, [value, users]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  // Keep activeIdx in bounds when filtered list changes shape (e.g. user types
  // a query that narrows the results past the previously highlighted index).
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(filtered.length - 1);
  }, [filtered.length, activeIdx]);

  const handleSelect = (user) => {
    const id = user.user_id || user.email;
    onChange(id);
    setSelectedName(user.name || user.email || "");
    setSearch("");
    setShowDropdown(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setShowDropdown(true);
      e.preventDefault();
      return;
    }
    if (!showDropdown || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = activeIdx >= 0 ? activeIdx : 0;
      const u = filtered[idx];
      if (u) handleSelect(u);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setActiveIdx(-1);
    }
  };

  // Auto-scroll the active row into view when keyboard navigation pushes it
  // off-screen.
  useEffect(() => {
    if (activeIdx < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(`[data-row-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {label && <label style={{display:"block",fontSize:"0.875rem",fontWeight:500,color:"var(--t2)",marginBottom:"4px"}}>{label}{required && <span style={{color:"#f87171",marginLeft:"4px"}}>*</span>}</label>}
      <input
        type="text"
        className="w-full px-3 py-2 rounded-lg bg-[var(--s1)] border border-[var(--bdr)] text-[var(--t1)] font-[var(--font-mono)] text-sm focus:outline-none focus:border-[var(--c)]"
        placeholder={placeholder}
        value={showDropdown ? search : (selectedName || search)}
        onChange={e => { setSearch(e.target.value); setShowDropdown(true); setActiveIdx(-1); }}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
      />
      {value && !showDropdown && (
        <button
          type="button"
          onClick={() => { onChange(""); setSelectedName(""); setSearch(""); }}
          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 14, fontFamily: "var(--font-mono)" }}
        >
          ✕
        </button>
      )}
      {showDropdown && (
        <div ref={dropdownRef} style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 9999,
          background: "var(--s1)",
          border: "1px solid var(--bdr)",
          borderRadius: 8,
          marginTop: 4,
          maxHeight: 200,
          overflowY: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "8px 12px", color: "var(--t3)", fontSize: 12, fontFamily: "var(--font-mono)" }}>No users found</div>
          ) : (
            filtered.map((u, idx) => {
              const isActive = idx === activeIdx;
              return (
                <div
                  key={u.email || u.user_id}
                  data-row-idx={idx}
                  onClick={() => handleSelect(u)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontFamily: "var(--font-mono)",
                    color: "var(--t1)",
                    borderBottom: "1px solid var(--bdr)",
                    background: isActive ? "var(--s2)" : "transparent",
                    transition: "background 0.1s",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{u.name || "Unnamed"}</div>
                  <div style={{ fontSize: 11, color: "var(--t3)" }}>{u.email}</div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
